import { PlayCircleOutlined } from '@ant-design/icons'
import {
  Alert,
  Button,
  Modal,
  Select,
  Table,
} from '@surecompanies/core_components'
import type { ColumnsType } from 'antd/es/table'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import Page from 'layouts/Page'
import React, { useState } from 'react'
import { browserHistory } from 'react-router'
import type { AuditRun, AuditRunStatus } from '../api/enrollment-overlap.types'
import QueryErrorState from '../components/QueryErrorState'
import RunStatusTag from '../components/RunStatusTag'
import { useRunsList } from '../hooks/useRunsList'
import { enrollmentOverlapRunPath } from '../paths'
import { handleRowLinkClick } from '../utils/link-click'
import { PAGE_SIZE_OPTIONS } from '../utils/paging'
import {
  formatCount,
  formatDateScope,
  formatDateTime,
  formatDuration,
  formatTrigger,
} from '../utils/run-display'

const ALL_STATUSES = 'ALL'

const STATUS_FILTER_OPTIONS: Array<{
  value: AuditRunStatus | typeof ALL_STATUSES
  label: string
}> = [
  { value: ALL_STATUSES, label: 'All' },
  { value: 'RUNNING', label: 'Running' },
  { value: 'SUCCEEDED', label: 'Succeeded' },
  { value: 'FAILED', label: 'Failed' },
]

const HIGHLIGHT_STYLE: React.CSSProperties = { background: '#e6f4ff' }

const openRun = (runId: string) =>
  browserHistory.push(enrollmentOverlapRunPath(runId))

export default function RunsList() {
  const {
    page,
    pageSize,
    status,
    setPaging,
    setStatus,
    runs,
    isLoading,
    error,
    refetch,
    lastSuccessfulRun,
    isLastSuccessfulLoading,
    highlightedRunId,
    isAuditRunning,
    isTriggering,
    runAuditNow,
  } = useRunsList()

  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const handleConfirmRun = () => {
    setIsConfirmOpen(false)
    runAuditNow()
  }

  const columns: ColumnsType<AuditRun> = [
    {
      title: 'Started',
      key: 'startedAt',
      render: (_, run) => (
        <a
          href={enrollmentOverlapRunPath(run.runId)}
          onClick={(event) =>
            handleRowLinkClick(event, () => openRun(run.runId))
          }
        >
          {formatDateTime(run.startedAt)}
        </a>
      ),
    },
    {
      title: 'Trigger',
      key: 'trigger',
      render: (_, run) => formatTrigger(run),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, run) => (
        <div>
          <RunStatusTag run={run} />
          {run.status === 'FAILED' && run.errorMessage ? (
            <div
              title={run.errorMessage}
              style={{
                color: '#cf1322',
                fontSize: 12,
                maxWidth: 280,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {run.errorMessage}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Date scope',
      key: 'dateScope',
      render: (_, run) =>
        formatDateScope(run.dateScopeStart, run.dateScopeEndExclusive),
    },
    {
      title: 'Employees with overlaps',
      key: 'employeeCount',
      align: 'right',
      render: (_, run) => formatCount(run.employeeCount),
    },
    {
      title: 'Overlapping pairs',
      key: 'pairCount',
      align: 'right',
      render: (_, run) => formatCount(run.pairCount),
    },
    {
      title: 'Duration',
      key: 'duration',
      align: 'right',
      render: (_, run) => formatDuration(run.startedAt, run.completedAt),
    },
  ]

  const hasFilter = status !== undefined
  const lastSuccessfulText = isLastSuccessfulLoading
    ? 'Loading…'
    : lastSuccessfulRun
      ? formatDateTime(lastSuccessfulRun.startedAt)
      : 'None yet'

  return (
    <Page
      title="Enrollment Overlap Audit"
      breadcrumb={
        <BreadcrumbNav
          items={[
            { title: 'Home', path: '/' },
            { title: 'Enrollment Overlap' },
          ]}
        />
      }
      action={
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          loading={isTriggering}
          disabled={isAuditRunning}
          title={
            isAuditRunning
              ? 'An audit is already running'
              : 'Find employees with overlapping enrollments now'
          }
          onClick={() => setIsConfirmOpen(true)}
        >
          Run audit now
        </Button>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Each report is a snapshot taken when the audit ran. An enrollment that is fixed afterwards still appears until the next audit run. The audit runs automatically every day at 02:00 PST."
      />

      {isAuditRunning ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="An audit is running. This list refreshes automatically when it finishes."
        />
      ) : null}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div data-testid="last-successful-run">
          <strong>Last successful run:</strong> {lastSuccessfulText}
        </div>
        <Select
          aria-label="Filter by status"
          value={status ?? ALL_STATUSES}
          options={STATUS_FILTER_OPTIONS}
          onChange={(value: AuditRunStatus | typeof ALL_STATUSES) =>
            setStatus(value === ALL_STATUSES ? undefined : value)
          }
          style={{ minWidth: 180 }}
        />
      </div>

      {error && !runs ? (
        <QueryErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <Table<AuditRun>
          dataSource={runs?.items ?? []}
          columns={columns}
          rowKey={(run) => run.runId}
          loading={isLoading}
          locale={{
            emptyText: hasFilter
              ? 'No runs match this status'
              : 'No audit runs yet',
          }}
          onRow={(run) => ({
            onClick: () => openRun(run.runId),
            style: {
              cursor: 'pointer',
              ...(run.runId === highlightedRunId ? HIGHLIGHT_STYLE : null),
            },
          })}
          pagination={{
            current: page,
            pageSize,
            total: runs?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            showTotal: (total) => `${total.toLocaleString()} runs`,
            onChange: (nextPage, nextPageSize) =>
              setPaging(nextPage, nextPageSize),
          }}
        />
      )}
      <Modal
        open={isConfirmOpen}
        title="Start a new audit?"
        okText="Run audit"
        cancelText="Cancel"
        onOk={handleConfirmRun}
        onCancel={() => setIsConfirmOpen(false)}
      >
        <p>
          This checks every employee for overlapping enrollments and can take a
          few minutes. Only one audit can run at a time.
        </p>
      </Modal>
    </Page>
  )
}
